import React, { useMemo, useState, useCallback } from 'react';
import { Badge, Drawer, Timeline, Tag, Tooltip, Typography } from 'antd';
import { SoundOutlined, RocketOutlined, ToolOutlined, BugOutlined, ExperimentOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useAppStore } from '../../../stores/useAppStore';
import { CURRENT_CHANGELOG_VERSION } from '../../../stores/slices/whatsNewSlice';
import { changelogEntries, type ChangelogEntry, type ChangeType } from './changelogData';

const { Text, Title } = Typography;

const typeConfig: Record<ChangeType, { color: string; icon: React.ReactNode; label: string }> = {
  feature: { color: 'blue', icon: <RocketOutlined />, label: 'Новое' },
  improvement: { color: 'cyan', icon: <ToolOutlined />, label: 'Улучшение' },
  fix: { color: 'green', icon: <BugOutlined />, label: 'Исправление' },
  experiment: { color: 'purple', icon: <ExperimentOutlined />, label: 'Бета' },
};

function ChangelogItem({ entry }: { entry: ChangelogEntry }) {
  const cfg = typeConfig[entry.type];
  return (
    <div style={{ marginBottom: 4 }}>
      <Tag color={cfg.color} icon={cfg.icon} style={{ margin: '0 0 4px' }}>
        {cfg.label}
      </Tag>
      <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 2 }}>
        {entry.title}
      </Text>
      {entry.description && (
        <Text type="secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
          {entry.description}
        </Text>
      )}
    </div>
  );
}

export const WhatsNew: React.FC = () => {
  const [open, setOpen] = useState(false);
  const lastSeenVersion = useAppStore((s) => s.lastSeenVersion);
  const setLastSeenVersion = useAppStore((s) => s.setLastSeenVersion);

  const hasUnread = useMemo(
    () => lastSeenVersion !== CURRENT_CHANGELOG_VERSION,
    [lastSeenVersion]
  );

  const handleOpen = useCallback(() => {
    setOpen(true);
    setLastSeenVersion(CURRENT_CHANGELOG_VERSION);
  }, [setLastSeenVersion]);

  const groupedEntries = useMemo(() => {
    const groups = new Map<string, ChangelogEntry[]>();
    for (const entry of changelogEntries) {
      const existing = groups.get(entry.version);
      if (existing) {
        existing.push(entry);
      } else {
        groups.set(entry.version, [entry]);
      }
    }
    return groups;
  }, []);

  return (
    <>
      <Tooltip title="Что нового?" placement="bottom">
        <motion.button
          className="header-button"
          onClick={handleOpen}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <Badge dot={hasUnread} offset={[-2, 2]}>
            <SoundOutlined style={{ fontSize: 18, color: 'white' }} />
          </Badge>
        </motion.button>
      </Tooltip>

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SoundOutlined style={{ fontSize: 18, color: 'var(--color-info)' }} />
            <span>Что нового?</span>
          </div>
        }
        open={open}
        onClose={() => setOpen(false)}
        width={420}
        styles={{ body: { padding: '16px 24px' } }}
      >
        {Array.from(groupedEntries.entries()).map(([version, entries]) => (
          <div key={version} style={{ marginBottom: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Title level={5} style={{ margin: 0 }}>
                {version}
              </Title>
              {version === CURRENT_CHANGELOG_VERSION && (
                <Tag color="blue" style={{ margin: 0 }}>Текущая</Tag>
              )}
            </div>
            <Timeline
              items={entries.map((entry, i) => ({
                key: `${version}-${i}`,
                color: typeConfig[entry.type].color,
                children: <ChangelogItem entry={entry} />,
              }))}
            />
          </div>
        ))}

        <div style={{ textAlign: 'center', padding: '16px 0', borderTop: '1px solid var(--color-border-primary)' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Все обновления начиная с 11 февраля 2026
          </Text>
        </div>
      </Drawer>
    </>
  );
};
